import { useEffect } from 'react';
import { Control, UseFormReset, UseFormSetValue, useWatch } from 'react-hook-form';

import { InquiryTemplateSimulateFormData } from 'dashboard/apps/InquiryTemplateEditor/InquiryPreview/types';
import { initialSimulateStubsAndPresets } from 'dashboard/apps/InquiryTemplateEditor/InquiryPreview/utils';
import { Inquiry } from 'dashboard/models/inquiry';
import { VerificationTemplateVersionStubs } from 'dashboard/models/simulate/stubs';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';
import { FieldValue } from 'lib/fields';
import { useFormSecurely } from 'lib/security/reactHookForm';

export const useSimulateAutoSaveForm = ({
  inquiry,
  verificationTemplateVersions,
  isAdvancedMode,
  handleSaveVerificationTemplateVersionStubs,
}: {
  inquiry: Inquiry | null;
  verificationTemplateVersions: Record<string, VerificationTemplateVersion>;
  isAdvancedMode: boolean;
  handleSaveVerificationTemplateVersionStubs: (
    inquiryId: string,
    verificationTemplateVersionStubs: VerificationTemplateVersionStubs[],
    fieldStubs: Record<string, FieldValue>,
  ) => void;
}): {
  control: Control<InquiryTemplateSimulateFormData>;
  reset: UseFormReset<InquiryTemplateSimulateFormData>;
  setValue: UseFormSetValue<InquiryTemplateSimulateFormData>;
  formValues: InquiryTemplateSimulateFormData;
} => {
  const { control, reset, setValue } = useFormSecurely<InquiryTemplateSimulateFormData>({
    mode: 'onSubmit',
    defaultValues: initialSimulateStubsAndPresets(verificationTemplateVersions, {
      isAdvancedMode,
      // To avoid "Type instantiation is excessively deep and possibly infinite"
    }) as object,
  });

  // Unlike watch(), useWatch() will return the same object reference for a hash if the
  // values are unchanged. This is important because `formValues` is used
  // in an effect below (which does only shallow comparison)
  const formValues = useWatch({
    control,
    // To avoid "Type instantiation is excessively deep and possibly infinite"
  }) as unknown as InquiryTemplateSimulateFormData;

  // Use an effect to auto-save stub changes as there is no way to otherwise trigger on form change
  useEffect(() => {
    if (inquiry?.id) {
      handleSaveVerificationTemplateVersionStubs(
        inquiry.id,
        formValues['verification-template-version-stubs'],
        formValues['fields-stubs'],
      );
    }
  }, [inquiry?.id, formValues, handleSaveVerificationTemplateVersionStubs]);

  return {
    control,
    reset,
    setValue,
    formValues,
  };
};
